import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useCopyLink } from "@/services/hooks/useCopyLink";
import DeleteEntityModal from "@/components/layouts/DeleteEntityModal";
import ToastBody from "@/components/ui/toast-body";
import {
  EntityType,
  useEntityType
} from "@/services/hooks/entities/useEntityType";
import {
  ConvertToContractData,
  DealCloseData,
  DealData,
  DealStatus
} from "@/pages/deals/types";
import { DealModalState } from "@/pages/deals/components/KanbanBoard/KanbanBoard";
import CloseDealModal from "@/pages/deals/components/CloseDealModal";
import { ConvertDealToContractModal } from "@/pages/deals/components/ConvertDealToContractModal";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { find, minBy } from "lodash";
import { KanbanContext } from "@/pages/deals/components/KanbanBoard/KanbanProvider";
import { removeDealFromBoard } from "@/pages/deals/helpers";
import { useCanCloseWon } from "@/services/hooks/entities/deal/useCanCloseWon";

export const useDealActions = (
  dealEntity: DealData | undefined,
  isKanbanLayout?: boolean,
  isDetailedView?: boolean,
  onClose?: () => void,
  onGuardrailsError?: (dealId: string, errorData: any) => void,
  forceRefetch?: boolean,
  includeAdditionals?: boolean
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleCopyLink = useCopyLink();
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get("view");
  const location = useLocation();

  const handleStatusUpdate = (options: {
    status: DealStatus;
    successToastText?: string;
    onSuccessFn?: () => void;
  }) =>
    onUpdateField(
      "status",
      options.status,
      undefined,
      options?.successToastText,
      options?.onSuccessFn,
      isDetailedView
    );

  const {
    deal,
    setDeal,
    resetDeals,
    onUpdateDealField: onUpdateField,
    deleteDeal
  } = useEntityType(
    EntityType.DEAL,
    dealEntity,
    isKanbanLayout,
    () => setIsOpenConfirmationModal(true),
    onGuardrailsError,
    forceRefetch,
    includeAdditionals
  );

  const updateDealState = useCallback(
    (updatedFields: Partial<DealData>) => {
      if (deal) {
        setDeal({
          ...deal,
          ...updatedFields
        });
      }
    },
    [deal, setDeal]
  );

  const stages = useDealStages().data?.stages;

  const [isOpenDeleteDealModal, setIsOpenDeleteDealModal] = useState(false);
  const [closeDealModalState, setCloseDealModalState] =
    useState<DealModalState>({
      itemToClose: null,
      isOpen: false,
      status: DealStatus.CLOSED_LOST
    });
  const [isTasksDrawerOpen, setIsTasksDrawerOpen] = useState(false);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);

  const toggleTasksDrawer = () => setIsTasksDrawerOpen(!isTasksDrawerOpen);

  const kanbanContext = useContext(KanbanContext);

  // The card leaves the board as soon as the close is confirmed rather than
  // when the save and the column refetch behind it finish, which can take
  // seconds; the returned function puts it back if the close isn't saved.
  const removeClosingDealFromBoard = () => {
    const dealId = closeDealModalState.itemToClose?.id ?? deal?.id;
    if (!isKanbanLayout || !kanbanContext || !dealId) return undefined;
    return removeDealFromBoard(
      kanbanContext.deals,
      kanbanContext.setDeals,
      dealId
    );
  };

  const handleCloseDeal = (
    data?: DealCloseData,
    isEditMode?: boolean,
    onClosedWonSuccess?: () => void
  ) => {
    const dealStage =
      closeDealModalState.itemToClose?.stage ?? deal?.stage ?? null;

    const successCallback = () => {
      if (closeDealModalState.status === DealStatus.CLOSED_WON) {
        onClosedWonSuccess?.();
      }
      if (
        isKanbanLayout &&
        dealStage &&
        kanbanContext?.columnRefetchFunctions[dealStage]
      ) {
        kanbanContext.columnRefetchFunctions[dealStage]();
      }
      onClose?.();
    };

    // Editing the reason of an already-closed deal is not a close
    const restoreDealOnBoard = isEditMode
      ? undefined
      : removeClosingDealFromBoard();

    onUpdateField(
      "status",
      closeDealModalState.status,
      data as { [fieldName: string]: string | number },
      undefined,
      successCallback,
      isDetailedView,
      isEditMode,
      undefined,
      restoreDealOnBoard
    );
    setCloseDealModalState({
      ...closeDealModalState,
      itemToClose: null,
      isOpen: false
    });
  };

  const handleConvertToContract = (
    contractData: ConvertToContractData,
    onClosedWonSuccess?: () => void
  ) => {
    const dealStage =
      closeDealModalState.itemToClose?.stage ?? deal?.stage ?? null;

    const successCallback = () => {
      onClosedWonSuccess?.();
      if (
        isKanbanLayout &&
        dealStage &&
        kanbanContext?.columnRefetchFunctions[dealStage]
      ) {
        kanbanContext.columnRefetchFunctions[dealStage]();
      }
      onClose?.();
    };

    onUpdateField(
      "status",
      DealStatus.CLOSED_WON,
      contractData as unknown as { [fieldName: string]: string | number },
      undefined,
      successCallback,
      isDetailedView,
      undefined,
      undefined,
      removeClosingDealFromBoard()
    );
    setCloseDealModalState({
      ...closeDealModalState,
      itemToClose: null,
      isOpen: false
    });
  };

  const setIsCloseModalOpen: React.Dispatch<React.SetStateAction<boolean>> = (
    value
  ) => {
    setCloseDealModalState((prevState) => ({
      ...prevState,
      isOpen: typeof value === "function" ? value(prevState.isOpen) : value
    }));
  };
  const firstStage = minBy(stages, "stageOrder");
  const currentStage = find(stages, (stage) => stage.id === deal?.stage);

  const isFirstStageDeal = currentStage?.id === firstStage?.id;
  const canCloseWon = useCanCloseWon();

  const handleDelete = (
    setIsOpenDeleteDealModal: Dispatch<SetStateAction<boolean>>,
    isQuickView: boolean = false,
    closeDrawer?: () => void
  ) => {
    if (deal) {
      deleteDeal.mutate(
        {
          pathParams: { dealId: deal.id }
        },
        {
          onSuccess: () => {
            if (isQuickView) {
              setIsOpenDeleteDealModal(false);
              closeDrawer?.();
            } else {
              navigate(viewParam ? `/deals?view=${viewParam}` : "/deals", { replace: true });
            }
            resetDeals();
            toast.success(
              <ToastBody title={`${t("deals.dealView.toast1")} ${deal.name} ${t("deals.dealView.toast2")}`} />,
              {
                duration: 5000,
                icon: null
              }
            );
          }
        }
      );
    }
  };

  const handleOpenCloseDealModal = (
    status: DealStatus.CLOSED_LOST | DealStatus.CLOSED_WON,
    selectedReason?: number | null,
    closedLostReasonText?: string | null
  ) => {
    setCloseDealModalState({
      itemToClose: deal as DealData,
      isOpen: true,
      status,
      selectedReason,
      closedLostReasonText
    });
  };

  const handleReopenDeal = () => {
    handleStatusUpdate({
      status: DealStatus.IN_PROGRESS,
      successToastText: t("deals.dealView.inProgressToast")
    });
    setIsOpenConfirmationModal(false);
  };

  const refetchKanbanColumns = (
    firstColumnId: number,
    secondColumnId: number
  ) => {
    if (kanbanContext?.columnRefetchFunctions[firstColumnId]) {
      kanbanContext?.columnRefetchFunctions[firstColumnId]();
    }
    if (kanbanContext?.columnRefetchFunctions[secondColumnId]) {
      kanbanContext?.columnRefetchFunctions[secondColumnId]();
    }
  };

  const getTopOptions = (isQuickView: boolean = false) => {
    if (!stages || !stages.length || !currentStage) {
      return {} as DetailedViewOptions;
    }
    const isInLastStage = stages?.[stages?.length - 1].id === currentStage?.id;
    const currentStageOrder = currentStage?.stageOrder || 1;
    const nextStage =
      find(stages, (stage) => stage.stageOrder === currentStageOrder + 1) ||
      null;

    const previousStage =
      find(stages, (stage) => stage.stageOrder === currentStageOrder - 1) ||
      null;

    const isInProgress = deal?.status === DealStatus.IN_PROGRESS;
    const isStuck = deal?.status === DealStatus.STUCK;
    const isClosedLost = deal?.status === DealStatus.CLOSED_LOST;
    const isClosedWon = deal?.status === DealStatus.CLOSED_WON;

    return {
      ...(isQuickView &&
        (isInProgress || isStuck) &&
        !isInLastStage && {
          proceedTo: {
            action: () => {
              onUpdateField(
                "stage",
                nextStage?.id || 2,
                undefined,
                undefined,
                isKanbanLayout
                  ? () =>
                      refetchKanbanColumns(currentStage?.id, nextStage?.id || 2)
                  : undefined
              );
            },
            translationVars: {
              stageName: nextStage?.name || "Next Stage"
            }
          }
        }),
      ...(isQuickView &&
        isInProgress &&
        isInLastStage && {
          moveBackTo: {
            action: () => {
              onUpdateField(
                "stage",
                previousStage?.id || 1,
                undefined,
                undefined,
                isKanbanLayout
                  ? () =>
                      refetchKanbanColumns(
                        currentStage?.id,
                        previousStage?.id || 1
                      )
                  : undefined
              );
            },
            translationVars: {
              stageName: previousStage?.name || "Previous Stage"
            }
          }
        }),
      ...(isQuickView &&
        (isInProgress || isStuck) && {
          closeWon: {
            action: () => handleOpenCloseDealModal(DealStatus.CLOSED_WON),
            disabled: isFirstStageDeal || !canCloseWon,
            disabledTooltipText: canCloseWon
              ? t("deals.dealView.disabledCloseWon")
              : t("deals.dealView.closeWonNotPermitted")
          },
          closeLost: () => handleOpenCloseDealModal(DealStatus.CLOSED_LOST)
        }),
      ...(isInProgress && {
        markAsStuck: () => {
          handleStatusUpdate({
            status: DealStatus.STUCK,
            successToastText: t("deals.dealView.markAsStuckToast")
          });
        }
      }),
      ...(isStuck && {
        unmarkAsStuck: () => {
          handleStatusUpdate({
            status: DealStatus.IN_PROGRESS,
            successToastText: t("deals.dealView.inProgressToast")
          });
        }
      }),
      ...((isClosedLost || isClosedWon) && {
        reopenDeal: () => setIsOpenConfirmationModal(true)
      })
    } as DetailedViewOptions;
  };

  const getOptions = (isQuickView: boolean = false, dealId?: string) => {
    return {
      copyLink: () => handleCopyLink(isQuickView, dealId),
      dealLog: () =>
        isQuickView
          ? navigate(`/deals/${dealId}/log${location.search}`, {
              replace: true
            })
          : navigate("log"),
      delete: {
        action: () => setIsOpenDeleteDealModal(true),
        variant: "destructive"
      }
    } as DetailedViewOptions;
  };

  const renderModals = (
    isDropdownClosing = false,
    isQuickView = false,
    onClose?: () => void,
    onClosedWonSuccess?: () => void
  ) => {
    const isClosedWonModal =
      closeDealModalState.status === DealStatus.CLOSED_WON &&
      closeDealModalState.selectedReason == null;

    return (
      <>
        <DeleteEntityModal
          isOpen={isOpenDeleteDealModal && !isDropdownClosing}
          setIsOpen={setIsOpenDeleteDealModal}
          entity={EntityType.DEAL}
          entityName={deal?.name || ""}
          handleDelete={() =>
            handleDelete(setIsOpenDeleteDealModal, isQuickView, onClose)
          }
        />
        {isClosedWonModal ? (
          <ConvertDealToContractModal
            isOpen={closeDealModalState.isOpen && !isDropdownClosing}
            setIsOpen={setIsCloseModalOpen}
            deal={closeDealModalState.itemToClose}
            onSubmit={(contractData) =>
              handleConvertToContract(contractData, onClosedWonSuccess)
            }
          />
        ) : (
          <CloseDealModal
            isOpen={closeDealModalState.isOpen && !isDropdownClosing}
            setIsOpen={setIsCloseModalOpen}
            itemToClose={closeDealModalState.itemToClose}
            closeDeal={(_item, _status, data, _sourceColumnId, isEditMode) =>
              handleCloseDeal(data, isEditMode, onClosedWonSuccess)
            }
            status={closeDealModalState.status}
            selectedReason={closeDealModalState.selectedReason}
            closedLostReasonText={closeDealModalState.closedLostReasonText}
          />
        )}
        <ConfirmationModal
          isOpen={isOpenConfirmationModal && !isDropdownClosing}
          setIsOpen={setIsOpenConfirmationModal}
          title={t("deals.dealView.reopenDeal")}
          confirmationText={t("deals.dealView.reopenDealConfirmation")}
          handleSubmit={handleReopenDeal}
          isPrimaryButton
          submitButtonText={t("confirm")}
        />
      </>
    );
  };

  return {
    deal,
    updateDealState,
    viewParam,
    stages,
    isTasksDrawerOpen,
    toggleTasksDrawer,
    onUpdateField,
    getTopOptions,
    getOptions,
    renderModals,
    handleOpenCloseDealModal,
    currentStage,
    searchParams,
    isFirstStageDeal,
    canCloseWon
  };
};
